<script setup lang="ts">
/**
 * Admin tab — the feedback inbox, and the review loop that acts on it.
 *
 * Two halves, top to bottom:
 *
 *   1. **Inbox.** What users actually voted on, loaded the moment the
 *      tab opens. Counts come from `/api/admin/feedback/overview`
 *      (aggregated server-side, so they're the real totals, not the
 *      capped row list) and rows from `/api/admin/feedback`. Rows an
 *      analyze run has never seen are flagged `NEW`. Nobody has to
 *      kick a run to find out whether there's anything to look at —
 *      that used to be the only way, and it left an empty run behind
 *      every time.
 *   2. **Review loop.** Same filters feed `Analyze`: cluster the
 *      failure modes, eyeball the suggested prompt edit, `Apply`, then
 *      `Retest` the same questions to confirm the fix landed.
 *
 * Run state is polled from /api/admin/feedback-review/runs/{id} until
 * `status` reaches a terminal state (`ready`, `applied`, `complete`,
 * `error`). 1.5 s poll interval keeps the UI responsive without
 * hammering the backend. The inbox refreshes on its own slower timer
 * so new votes surface while the tab sits open.
 */
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import {
  AdminError,
  type AssistantRow,
  type FeedbackOverview,
  type FeedbackReviewRun,
  type FeedbackReviewRunSummary,
  type FeedbackRow,
  applyFeedbackReviewPrompt,
  createFeedbackReviewRun,
  getFeedbackOverview,
  markFeedbackReviewed,
  getFeedbackReviewRun,
  kickFeedbackReviewRetest,
  listAssistants,
  listFeedback,
  listFeedbackReviewRuns,
} from "../api/admin";
import { useToast } from "../composables/useToast";
import { useConfirm } from "../composables/useConfirm";
import HelpHint from "./HelpHint.vue";
import SkeletonRows from "./SkeletonRows.vue";

/** How often the inbox re-reads itself while the tab is open. */
const INBOX_REFRESH_MS = 60_000;

const props = defineProps<{ devUser: string }>();
const toast = useToast();
const { confirm } = useConfirm();

const assistants = ref<AssistantRow[]>([]);
const runs = ref<FeedbackReviewRunSummary[]>([]);
const selectedRunId = ref<number | null>(null);
const run = ref<FeedbackReviewRun | null>(null);

// Filters. They drive the inbox *and* the analyze run, so what you see
// is what you'd analyze. Empty slug = every assistant (the landing
// state — "is there anything new anywhere?").
const selectedSlug = ref<string>("");
const kindFilter = ref<"" | "down" | "up">("down");
const sinceInput = ref("14d");
const limitInput = ref(200);
/**
 * Narrow the list to the rows the "new since last review" card counts.
 *
 * That card counts thumbs-up and thumbs-down, of any age. The list below
 * it defaults to thumbs-down in the last 14 days. So the card could say
 * "3 new" over a list with none in it, and the only way to find them was
 * to guess which filter was hiding them — reported 2026-10-02, after the
 * same three had survived a "clear all" days earlier (that clears the
 * assistant in the dropdown, not every assistant).
 */
const onlyNew = ref(false);

// Inbox state.
const overview = ref<FeedbackOverview | null>(null);
/**
 * The same counts with no lower bound.
 *
 * The card says "new since last review", not "new in the last 14d" — and it
 * was only ever counting the window, so unreviewed feedback older than the
 * filter was invisible here while still sitting in the tab badge, which has
 * no window. Two different questions were being shown as one number.
 * `overview` still drives the thumbs-up/down cards, which do say "last 14d".
 */
const allTimeOverview = ref<FeedbackOverview | null>(null);
const feedbackRows = ref<FeedbackRow[]>([]);
const inboxLoading = ref(false);
const expandedRows = ref<Set<number>>(new Set());

// The operator can tweak the suggested prompt before apply. We seed
// this from `run.payload.suggestion.new_system_prompt` whenever the
// run flips to `ready`.
const editedPrompt = ref<string>("");

const busy = ref(false);
const pollHandle = ref<number | null>(null);
const inboxHandle = ref<number | null>(null);

// Which verdict rows are expanded (showing old/new answer + complaint).
const expandedVerdicts = ref<Set<number>>(new Set());
function toggleVerdict(id: number): void {
  const next = new Set(expandedVerdicts.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  expandedVerdicts.value = next;
}

function toggleRow(id: number): void {
  const next = new Set(expandedRows.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  expandedRows.value = next;
}

// The prompt that was live before this run's edit. Shown side-by-side
// with the editable suggestion so the operator sees exactly what changes.
const currentPrompt = computed(() => run.value?.payload.current_prompt ?? "");

const isAnalyzing = computed(() => run.value?.status === "analyzing");
const isReady = computed(() => run.value?.status === "ready");
const isApplied = computed(() => run.value?.status === "applied");
const isRetesting = computed(() => run.value?.status === "retesting");
const isComplete = computed(() => run.value?.status === "complete");
const hasError = computed(() => run.value?.status === "error");

const terminalStatuses = new Set(["ready", "applied", "complete", "error"]);

/** Per-assistant counts for whatever is selected, or the grand total
 *  when the filter is "all assistants". */
/** `scopedCounts`, but over everything ever recorded — what "new" means. */
const scopedNew = computed(() => {
  const ov = allTimeOverview.value;
  if (!ov) return 0;
  if (!selectedSlug.value) return ov.totals.new_total;
  return (
    ov.assistants.find((a) => a.slug === selectedSlug.value)?.new_total ?? 0
  );
});

const scopedCounts = computed(() => {
  if (!overview.value) return null;
  if (!selectedSlug.value) return overview.value.totals;
  return (
    overview.value.assistants.find((a) => a.slug === selectedSlug.value) ?? {
      total: 0,
      down: 0,
      up: 0,
      new_total: 0,
      new_down: 0,
      new_up: 0,
    }
  );
});

/** The rows the table renders. `is_new` is stamped per row by the
 *  endpoint, so "only new" is a filter over what we already hold. */
const visibleRows = computed(() =>
  onlyNew.value
    ? feedbackRows.value.filter((r) => r.is_new)
    : feedbackRows.value,
);

/** Show exactly what the card counts: both kinds, no window, new only. */
function showNewOnly(): void {
  onlyNew.value = true;
  // Both of these are watched; changing either reloads the list.
  kindFilter.value = "";
  sinceInput.value = "";
}

/** How many thumbs-down `Analyze` would actually pull — the window
 *  count, capped by `limit`, so the button never promises more than
 *  the run will look at. */
const analyzableCount = computed(() =>
  Math.min(scopedCounts.value?.down ?? 0, limitInput.value || 0),
);

const analyzeDisabled = computed(
  () =>
    busy.value ||
    !selectedSlug.value ||
    isAnalyzing.value ||
    analyzableCount.value === 0,
);

const analyzeHint = computed(() => {
  if (!selectedSlug.value) return "Pick one assistant to analyze.";
  if (analyzableCount.value === 0) {
    return `No thumbs-down for this assistant in the last ${sinceInput.value} — widen the window.`;
  }
  return "";
});

const lastReviewedAt = computed(() => {
  if (!overview.value || !selectedSlug.value) return null;
  return (
    overview.value.assistants.find((a) => a.slug === selectedSlug.value)
      ?.last_reviewed_at ?? null
  );
});

function _toastError(e: unknown, ctx: string): void {
  const msg = e instanceof AdminError ? e.message : String(e);
  toast.error(ctx, msg);
}

function fmt(ts: string | null | undefined): string {
  return ts ? new Date(ts).toLocaleString() : "—";
}

function assistantName(slug: string): string {
  return assistants.value.find((a) => a.slug === slug)?.name || slug;
}

async function loadAssistants() {
  try {
    assistants.value = await listAssistants(props.devUser, false);
  } catch (e) {
    _toastError(e, "Load assistants");
  }
}

/** Counts + rows for the current filters. Silent on the timer refresh
 *  so a transient blip doesn't stack toasts on an idle tab. */
const marking = ref(false);

/**
 * Mark one row, or everything the inbox is currently showing (ADR 0056).
 *
 * Most feedback is read and acted on directly; the loop is for the cases
 * worth a prompt change. Marking sends the SAME filters the list was built
 * from, so "mark all" can only ever affect what is on screen — marking the
 * whole table because someone had filtered to one assistant would bury
 * other assistants' feedback without showing them.
 */
async function markReviewed(row?: FeedbackRow, reviewed = true) {
  if (marking.value) return;
  if (!row) {
    const n = scopedNew.value;
    const scope = selectedSlug.value
      ? assistantName(selectedSlug.value)
      : "every assistant";
    const ok = await confirm({
      title: `Clear ${n} new?`,
      message:
        `Every unreviewed thumbs-up and thumbs-down for ${scope}, of any ` +
        "age — not just the window above. They stay in the list and stop " +
        "counting as new, here and on the tab badge. You can put any of " +
        "them back individually.",
      confirmLabel: `Clear ${n}`,
    });
    if (!ok) return;
  }
  marking.value = true;
  try {
    const res = await markFeedbackReviewed(
      row
        ? { feedback_id: row.id, reviewed }
        : {
            // Deliberately no `kind`: this clears the card's number, and
            // that number counts thumbs-up and thumbs-down together.
            // No `kind` and no `since`: "clear all" means every unreviewed
            // row for this assistant scope, of any age. Bounding it by the
            // filter is how months-old feedback stays new forever while the
            // badge keeps counting it.
            assistant_slug: selectedSlug.value || undefined,
            reviewed: true,
          },
      props.devUser,
    );
    toast.success(
      res.changed === 0
        ? "Nothing to change"
        : reviewed
          ? `${res.changed} marked as reviewed`
          : `${res.changed} back in the queue`,
    );
    await loadInbox({ quiet: true });
    // The tab badge is polled on a 2-minute timer, so without this it keeps
    // showing the count from before the click — the nav disagreeing with the
    // list it links to, which is the thing the badge exists to avoid.
    window.dispatchEvent(new CustomEvent("choir:admin-counts-stale"));
  } catch (e) {
    _toastError(e, "Could not update that feedback");
  } finally {
    marking.value = false;
  }
}

async function loadInbox(opts: { quiet?: boolean } = {}) {
  inboxLoading.value = feedbackRows.value.length === 0;
  try {
    const [ov, allTime, page] = await Promise.all([
      getFeedbackOverview(sinceInput.value, props.devUser),
      getFeedbackOverview("", props.devUser),
      listFeedback(
        {
          assistantSlug: selectedSlug.value || undefined,
          kind: kindFilter.value || undefined,
          since: sinceInput.value,
          limit: limitInput.value,
        },
        props.devUser,
      ),
    ]);
    overview.value = ov;
    allTimeOverview.value = allTime;
    feedbackRows.value = page.rows;
  } catch (e) {
    if (!opts.quiet) _toastError(e, "Load feedback");
  } finally {
    inboxLoading.value = false;
  }
}

async function loadRuns() {
  if (!selectedSlug.value) {
    runs.value = [];
    selectedRunId.value = null;
    run.value = null;
    return;
  }
  try {
    runs.value = await listFeedbackReviewRuns(
      selectedSlug.value,
      props.devUser,
    );
    // If a run was already selected, refresh it; else pick the latest.
    if (
      selectedRunId.value &&
      runs.value.find((r) => r.id === selectedRunId.value)
    ) {
      await loadRun(selectedRunId.value);
    } else if (runs.value.length > 0) {
      selectedRunId.value = runs.value[0].id;
      await loadRun(runs.value[0].id);
    } else {
      selectedRunId.value = null;
      run.value = null;
    }
  } catch (e) {
    _toastError(e, "Load runs");
  }
}

async function loadRun(id: number) {
  try {
    const fresh = await getFeedbackReviewRun(id, props.devUser);
    run.value = fresh;
    // Reseed the editable prompt buffer when we get a fresh suggestion.
    if (fresh.payload.suggestion && !editedPrompt.value) {
      editedPrompt.value = fresh.payload.suggestion.new_system_prompt;
    }
    // Stop polling once we're at a terminal status.
    if (terminalStatuses.has(fresh.status) && pollHandle.value !== null) {
      window.clearInterval(pollHandle.value);
      pollHandle.value = null;
      // A finished run moves the "already reviewed" watermark, so the
      // NEW flags in the inbox are stale — re-read them.
      void loadInbox({ quiet: true });
    }
  } catch (e) {
    _toastError(e, "Load run");
  }
}

function startPolling(id: number) {
  if (pollHandle.value !== null) {
    window.clearInterval(pollHandle.value);
  }
  pollHandle.value = window.setInterval(() => {
    void loadRun(id);
  }, 1500);
}

async function startAnalyze() {
  if (!selectedSlug.value) return;
  busy.value = true;
  editedPrompt.value = "";
  try {
    const { run_id } = await createFeedbackReviewRun(
      selectedSlug.value,
      { since: sinceInput.value, limit: limitInput.value },
      props.devUser,
    );
    selectedRunId.value = run_id;
    await loadRuns();
    startPolling(run_id);
    toast.info("Analyze started", `Run #${run_id}`);
  } catch (e) {
    _toastError(e, "Start analyze");
  } finally {
    busy.value = false;
  }
}

async function applyPrompt() {
  if (!run.value) return;
  const prompt = editedPrompt.value.trim();
  if (!prompt) {
    toast.error("Apply prompt", "Prompt is empty");
    return;
  }
  const ok = await confirm({
    title: "Apply prompt to assistant?",
    message:
      `This overwrites the live system prompt for "${run.value.assistant_slug}" ` +
      "immediately. It takes effect on the next chat turn.",
    confirmLabel: "Apply",
    danger: true,
  });
  if (!ok) return;
  busy.value = true;
  try {
    await applyFeedbackReviewPrompt(run.value.id, prompt, props.devUser);
    toast.info(
      "Prompt applied",
      `Assistant ${run.value.assistant_slug} updated`,
    );
    await loadRun(run.value.id);
  } catch (e) {
    _toastError(e, "Apply prompt");
  } finally {
    busy.value = false;
  }
}

async function startRetest() {
  if (!run.value) return;
  busy.value = true;
  try {
    await kickFeedbackReviewRetest(run.value.id, props.devUser);
    toast.info("Retest started", `Run #${run.value.id}`);
    startPolling(run.value.id);
  } catch (e) {
    _toastError(e, "Start retest");
  } finally {
    busy.value = false;
  }
}

/** Clicking an assistant card in the header scopes every filter to it. */
function focusAssistant(slug: string) {
  selectedSlug.value = selectedSlug.value === slug ? "" : slug;
}

watch(selectedSlug, async () => {
  selectedRunId.value = null;
  run.value = null;
  editedPrompt.value = "";
  await Promise.all([loadInbox(), loadRuns()]);
});

watch([kindFilter, sinceInput], () => {
  void loadInbox();
});

watch(selectedRunId, async (id) => {
  if (id !== null) await loadRun(id);
});

onMounted(async () => {
  await loadAssistants();
  await Promise.all([loadInbox(), loadRuns()]);
  inboxHandle.value = window.setInterval(() => {
    void loadInbox({ quiet: true });
  }, INBOX_REFRESH_MS);
});
onUnmounted(() => {
  if (pollHandle.value !== null) window.clearInterval(pollHandle.value);
  if (inboxHandle.value !== null) window.clearInterval(inboxHandle.value);
});
</script>

<template>
  <div class="feedback-review">
    <header class="feedback-review__head">
      <h2>
        Feedback Review
        <HelpHint wide align="start" label="About feedback review">
          Every thumbs-up / thumbs-down users left, newest first —
          <strong>NEW</strong> marks the ones nobody has reviewed yet, by an
          analyze run or by hand. Narrow it to one assistant and a time window,
          then cluster the failure modes, apply a prompt edit, and retest the
          same questions. Backed by the same Python pipeline as
          <code>choir feedback</code> on the CLI.
        </HelpHint>
      </h2>
    </header>

    <!-- ── What came in ─────────────────────────────────────────────── -->
    <section class="inbox">
      <div class="summary" v-if="scopedCounts">
        <div
          class="summary__card summary__card--new"
          :data-empty="scopedNew === 0"
        >
          <span class="summary__n">{{ scopedNew }}</span>
          <span class="summary__label">new since last review</span>
          <span class="summary__sub">
            {{
              selectedSlug
                ? `any age · ${assistantName(selectedSlug)} only`
                : "any age · every assistant"
            }}
          </span>
          <button
            v-if="scopedNew > 0"
            type="button"
            class="link summary__action"
            @click="showNewOnly"
          >
            show them
          </button>
          <button
            v-if="scopedNew > 0"
            type="button"
            class="link summary__action"
            :disabled="marking"
            @click="markReviewed()"
          >
            {{ marking ? "clearing…" : "clear all" }}
          </button>
        </div>
        <div class="summary__card">
          <span class="summary__n">{{ scopedCounts.down }}</span>
          <span class="summary__label">thumbs-down</span>
          <span class="summary__sub">last {{ sinceInput }}</span>
        </div>
        <div class="summary__card">
          <span class="summary__n">{{ scopedCounts.up }}</span>
          <span class="summary__label">thumbs-up</span>
          <span class="summary__sub">last {{ sinceInput }}</span>
        </div>
        <div class="summary__card summary__card--meta">
          <span class="summary__label">
            {{ selectedSlug ? assistantName(selectedSlug) : "All assistants" }}
          </span>
          <span class="summary__sub" v-if="selectedSlug">
            last analyzed {{ fmt(lastReviewedAt) }}
          </span>
          <span class="summary__sub" v-else>
            pick one below to analyze it
          </span>
        </div>
      </div>

      <!-- Per-assistant breakdown: who has unreviewed complaints piling
           up. Click to scope every filter to that assistant. -->
      <div
        v-if="overview && overview.assistants.length > 1"
        class="by-assistant"
      >
        <button
          v-for="a in overview.assistants"
          :key="a.slug"
          type="button"
          class="chip"
          :class="{
            'chip--active': a.slug === selectedSlug,
            'chip--hot': a.new_down > 0,
          }"
          :aria-pressed="a.slug === selectedSlug"
          @click="focusAssistant(a.slug)"
        >
          {{ a.name }}
          <span v-if="a.new_down > 0" class="chip__badge"
            >{{ a.new_down }} new 👎</span
          >
          <span v-else class="chip__badge chip__badge--quiet"
            >{{ a.down }} 👎</span
          >
        </button>
      </div>

      <div class="filters">
        <label>
          Assistant
          <select v-model="selectedSlug">
            <option value="">All assistants</option>
            <option v-for="a in assistants" :key="a.slug" :value="a.slug">
              {{ a.name }} ({{ a.slug }})
            </option>
          </select>
        </label>
        <label>
          Show
          <select v-model="kindFilter">
            <option value="down">Thumbs-down</option>
            <option value="up">Thumbs-up</option>
            <option value="">Both</option>
          </select>
        </label>
        <label>
          Since
          <input
            v-model="sinceInput"
            type="text"
            placeholder="14d, ISO, or blank for all"
            title="A window like 14d or 48h, an ISO timestamp, or blank for everything ever recorded"
          />
        </label>
        <label>
          Limit
          <input v-model.number="limitInput" type="number" min="1" max="2000" />
        </label>
        <label class="filters__check" title="Only rows still counted as new">
          <input v-model="onlyNew" type="checkbox" />
          Only new
        </label>
        <button class="btn" :disabled="inboxLoading" @click="loadInbox()">
          Refresh
        </button>
      </div>

      <table v-if="!inboxLoading" class="admin-table feedback-table">
        <thead>
          <tr>
            <th></th>
            <th>When</th>
            <th>Assistant</th>
            <th>Who</th>
            <th>Question</th>
            <th>Comment</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="r in visibleRows" :key="r.id">
            <tr :class="{ 'is-new': r.is_new }">
              <td class="vote">
                <span v-if="r.kind === 'down'" title="thumbs-down">👎</span>
                <span v-else-if="r.kind === 'up'" title="thumbs-up">👍</span>
                <span v-else>{{ r.kind }}</span>
              </td>
              <td class="mono nowrap">
                {{ fmt(r.updated_at) }}
                <span v-if="r.is_new" class="badge-new">NEW</span>
                <span
                  v-else-if="r.reviewed_at"
                  class="badge-done"
                  :title="`Marked reviewed by ${r.reviewed_by || 'an admin'}`"
                >
                  reviewed
                </span>
                <span
                  v-if="r.replied_at"
                  class="badge-done"
                  :title="`Replied ${fmt(r.replied_at)} by ${r.replied_by || 'an admin'}`"
                >
                  replied
                </span>
              </td>
              <td>{{ assistantName(r.assistant_slug) }}</td>
              <td class="who">
                {{ r.user_email || r.user_display_name || "—" }}
              </td>
              <td class="q">{{ r.prior_user_content || "—" }}</td>
              <td class="c">{{ r.comment || "—" }}</td>
              <td>
                <div class="row-actions">
                  <button
                    type="button"
                    class="link"
                    :aria-expanded="expandedRows.has(r.id)"
                    @click="toggleRow(r.id)"
                  >
                    {{ expandedRows.has(r.id) ? "hide" : "answer" }}
                  </button>
                  <button
                    v-if="r.reviewed_at"
                    type="button"
                    class="link"
                    :disabled="marking"
                    title="Put this back in the queue"
                    @click="markReviewed(r, false)"
                  >
                    undo
                  </button>
                  <button
                    v-else-if="r.is_new"
                    type="button"
                    class="link"
                    :disabled="marking"
                    title="Stop this counting as new"
                    @click="markReviewed(r, true)"
                  >
                    mark read
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="expandedRows.has(r.id)" class="row-detail">
              <td colspan="7">
                <h5>Answer that was rated</h5>
                <pre>{{ r.message_content || "—" }}</pre>
                <template v-if="r.replied_at">
                  <h5>
                    Reply sent {{ fmt(r.replied_at) }} by
                    {{ r.replied_by || "an admin" }}
                  </h5>
                  <pre>{{ r.reply_subject }}

{{ r.reply_text }}</pre>
                </template>
              </td>
            </tr>
          </template>
          <tr v-if="visibleRows.length === 0">
            <td colspan="7" class="muted">
              <template v-if="onlyNew && feedbackRows.length > 0">
                Nothing new in this window — {{ feedbackRows.length }} row(s)
                here have all been reviewed. The card counts any age, so clear
                <code>since</code> to reach the rest.
              </template>
              <template v-else>
                No feedback matches these filters. Nothing to review — try a
                wider <code>since</code> if you expected some.
              </template>
            </td>
          </tr>
        </tbody>
      </table>
      <SkeletonRows v-else :rows="6" />
    </section>

    <!-- ── Act on it ────────────────────────────────────────────────── -->
    <section class="feedback-review__analyze">
      <button
        class="btn btn--primary"
        :disabled="analyzeDisabled"
        @click="startAnalyze"
      >
        Analyze
        <template v-if="selectedSlug">
          {{ analyzableCount }} thumbs-down for
          {{ assistantName(selectedSlug) }}
        </template>
      </button>
      <span v-if="analyzeHint" class="muted">{{ analyzeHint }}</span>
    </section>

    <section v-if="runs.length > 0" class="feedback-review__runs">
      <label>
        Run
        <select v-model="selectedRunId">
          <option v-for="r in runs" :key="r.id" :value="r.id">
            #{{ r.id }} — {{ r.status }} — {{ r.n_negative }} negative ·
            {{ r.n_clusters }} clusters ·
            {{ new Date(r.created_at).toLocaleString() }}
          </option>
        </select>
      </label>
    </section>

    <section v-if="run" class="feedback-review__detail">
      <div class="status-badge" :data-status="run.status">
        <strong>Status:</strong> {{ run.status }}
        <span
          v-if="isAnalyzing || isRetesting"
          class="spinner"
          aria-label="running…"
          >…</span
        >
      </div>

      <div v-if="hasError" class="error">
        <strong>Error:</strong> {{ run.payload.error_message }}
      </div>

      <!-- Clusters -->
      <div
        v-if="run.payload.clusters && run.payload.clusters.length > 0"
        class="clusters"
      >
        <h3>Failure-mode clusters ({{ run.payload.clusters.length }})</h3>
        <article v-for="c in run.payload.clusters" :key="c.id" class="cluster">
          <header>
            <strong>{{ c.title }}</strong>
            <span class="muted"
              >({{ c.id }}, {{ c.feedback_ids.length }} events)</span
            >
          </header>
          <p v-if="c.description">
            <em>What's happening:</em> {{ c.description }}
          </p>
          <p v-if="c.root_cause_hypothesis">
            <em>Root cause:</em> {{ c.root_cause_hypothesis }}
          </p>
          <p v-if="c.suggested_fix">
            <em>Direction of travel:</em> {{ c.suggested_fix }}
          </p>
        </article>
      </div>
      <div v-else-if="isReady || isApplied || isComplete" class="muted">
        No clusters surfaced. Either there's no negative feedback in scope (try
        a wider <code>since</code>), or every complaint was a one-off the
        analyzer couldn't generalise.
      </div>

      <!-- Prompt suggestion + apply -->
      <div v-if="run.payload.suggestion" class="suggestion">
        <h3>Suggested system prompt</h3>
        <p v-if="run.payload.suggestion.rationale" class="rationale">
          <strong>Rationale:</strong> {{ run.payload.suggestion.rationale }}
        </p>
        <details v-if="(run.payload.suggestion.out_of_scope || []).length">
          <summary>Out of scope for a prompt-only fix</summary>
          <ul>
            <li
              v-for="(item, i) in run.payload.suggestion.out_of_scope"
              :key="i"
            >
              {{ item }}
            </li>
          </ul>
        </details>
        <!-- Before/after comparison: the live prompt vs the edited
             suggestion, so the operator sees exactly what Apply will
             change. `current_prompt` is already on the payload. -->
        <div v-if="currentPrompt" class="prompt-compare">
          <div class="prompt-compare__col">
            <h4 class="prompt-compare__label">Current prompt (live)</h4>
            <pre class="prompt-compare__pre prompt-compare__pre--old">{{
              currentPrompt
            }}</pre>
          </div>
          <div class="prompt-compare__col">
            <h4 class="prompt-compare__label">New prompt (editable below)</h4>
            <pre class="prompt-compare__pre prompt-compare__pre--new">{{
              editedPrompt
            }}</pre>
          </div>
        </div>
        <textarea
          v-model="editedPrompt"
          class="prompt-editor"
          rows="14"
          spellcheck="false"
          :disabled="busy"
        />
        <div class="actions">
          <button
            class="btn btn--primary"
            :disabled="busy || !editedPrompt.trim() || hasError || isAnalyzing"
            @click="applyPrompt"
          >
            Apply prompt to assistant
          </button>
          <button
            class="btn"
            :disabled="busy || (!isApplied && !isComplete) || isRetesting"
            @click="startRetest"
          >
            Retest the {{ run.payload.rows?.length ?? 0 }} captured questions
          </button>
        </div>
      </div>

      <!-- Retest verdicts -->
      <div
        v-if="run.payload.verdicts && run.payload.verdicts.length > 0"
        class="verdicts"
      >
        <h3>
          Retest verdicts —
          {{ run.payload.n_fixed ?? 0 }} of
          {{ run.payload.n_retested ?? 0 }} fixed
        </h3>
        <table>
          <thead>
            <tr>
              <th>Score</th>
              <th>Fixed?</th>
              <th>Question</th>
              <th>Judge says</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="v in run.payload.verdicts" :key="v.feedback_id">
              <tr>
                <td>{{ v.improvement }}/5</td>
                <td>
                  <span v-if="v.fixed" title="fixed">✅</span>
                  <span v-else-if="v.errored" title="errored">⚠️</span>
                  <span v-else title="not fixed">❌</span>
                </td>
                <td class="q">{{ v.question }}</td>
                <td class="j">{{ v.explanation }}</td>
                <td>
                  <button
                    type="button"
                    class="link"
                    :aria-expanded="expandedVerdicts.has(v.feedback_id)"
                    @click="toggleVerdict(v.feedback_id)"
                  >
                    {{
                      expandedVerdicts.has(v.feedback_id) ? "hide" : "compare"
                    }}
                  </button>
                </td>
              </tr>
              <tr
                v-if="expandedVerdicts.has(v.feedback_id)"
                class="verdict-detail"
              >
                <td colspan="5">
                  <div class="verdict-detail__grid">
                    <div
                      v-if="v.original_complaint"
                      class="verdict-detail__cell"
                    >
                      <h5>Original complaint</h5>
                      <p>{{ v.original_complaint }}</p>
                    </div>
                    <div class="verdict-detail__cell">
                      <h5>Old answer</h5>
                      <pre>{{ v.original_answer || "—" }}</pre>
                    </div>
                    <div class="verdict-detail__cell">
                      <h5>New answer</h5>
                      <pre>{{ v.new_answer || "—" }}</pre>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Negative-feedback rows (collapsible — operator can verify clustering) -->
      <details
        v-if="run.payload.rows && run.payload.rows.length > 0"
        class="rows"
      >
        <summary>
          Source negative feedback ({{ run.payload.rows.length }} rows)
        </summary>
        <article v-for="r in run.payload.rows" :key="r.feedback_id" class="row">
          <header>
            <strong>{{ r.user_email || "(unknown)" }}</strong>
            <span class="muted">{{ r.updated_at }}</span>
          </header>
          <p><em>Q:</em> {{ r.user_question }}</p>
          <p v-if="r.comment"><em>Complaint:</em> {{ r.comment }}</p>
          <details>
            <summary>Original answer</summary>
            <pre>{{ r.assistant_answer }}</pre>
          </details>
        </article>
      </details>
    </section>
  </div>
</template>

<style scoped>
/* All colours route through the `--color-*` brand tokens so the tab
   stays white-label-safe (no hardcoded blues). Buttons use the shared
   `.btn*` system from styles/buttons.css. */
.feedback-review {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.feedback-review__head h2 {
  margin: 0 0 var(--space-1);
}
.hint {
  color: var(--color-muted);
  margin: 0;
  font-size: 0.9rem;
}
.inbox {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
/* Counts first: the answer to "is there anything new?" has to be
   readable from across the desk, before any filter is touched. */
.summary {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}
.summary__card {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 14ch;
  padding: var(--space-2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface-alt);
}
.summary__card--new {
  border-color: var(--color-accent);
  background: color-mix(in srgb, var(--color-accent) 10%, transparent);
}
/* Nothing new is the calm state — drop the accent so it doesn't cry wolf. */
.summary__card--new[data-empty="true"] {
  border-color: var(--color-border);
  background: var(--color-surface-alt);
}
.summary__card--meta {
  justify-content: center;
}
.summary__n {
  font-size: 1.6rem;
  font-weight: 600;
  line-height: 1.1;
}
.summary__label {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-muted);
}
.summary__sub {
  font-size: 0.8rem;
  color: var(--color-muted);
}
.by-assistant {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  font: inherit;
  font-size: 0.85rem;
  border: 1px solid var(--color-border);
  border-radius: 999px;
  background: var(--color-surface);
  color: var(--color-ink);
  cursor: pointer;
}
.chip:hover {
  border-color: var(--color-accent);
}
.chip--active {
  border-color: var(--color-accent);
  background: color-mix(in srgb, var(--color-accent) 12%, transparent);
}
.chip__badge {
  font-size: 0.75rem;
  padding: 1px 6px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--color-accent) 18%, transparent);
  color: var(--color-ink);
}
.chip__badge--quiet {
  background: var(--color-surface-alt);
  color: var(--color-muted);
}
.feedback-review__runs select {
  padding: 6px 10px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-ink);
  min-width: 12ch;
}
.feedback-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
}
.feedback-table th,
.feedback-table td {
  border: 1px solid var(--color-border);
  padding: 6px 10px;
  text-align: left;
  vertical-align: top;
}
.feedback-table th {
  background: var(--color-surface-alt);
}
.feedback-table tr.is-new > td {
  background: color-mix(in srgb, var(--color-accent) 6%, transparent);
}
.feedback-table td.vote {
  font-size: 1.05rem;
}
.feedback-table td.who {
  max-width: 22ch;
  overflow-wrap: anywhere;
}
.feedback-table td.q {
  max-width: 34ch;
}
.feedback-table td.c {
  max-width: 34ch;
}
.summary__action {
  margin-top: 0.35rem;
  font-size: 0.78rem;
}
.row-actions {
  display: flex;
  gap: 0.75rem;
  white-space: nowrap;
}
.badge-done {
  margin-left: 0.4rem;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--color-muted);
}
.badge-new {
  margin-left: 6px;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  padding: 1px 5px;
  border-radius: var(--radius-sm);
  background: var(--color-accent);
  color: var(--color-inverse, #fff);
}
.nowrap {
  white-space: nowrap;
}
.mono {
  font-family: var(--font-mono, monospace);
}
.row-detail > td {
  background: var(--color-surface-alt);
}
.row-detail h5 {
  margin: 0 0 4px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-muted);
}
.row-detail pre {
  margin: 0;
  font-family: var(--font-mono, monospace);
  font-size: 0.78rem;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 260px;
  overflow-y: auto;
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border);
  background: var(--color-surface);
}
.feedback-table .link {
  background: none;
  border: none;
  color: var(--color-accent);
  cursor: pointer;
  font: inherit;
  font-size: 0.85rem;
  padding: 0;
}
.feedback-review__analyze {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  flex-wrap: wrap;
  border-top: 1px solid var(--color-border);
  padding-top: var(--space-2);
}
.feedback-review__runs {
  margin-top: var(--space-2);
}
.feedback-review__runs select {
  min-width: 60ch;
}
.feedback-review__detail {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.status-badge {
  padding: 6px 12px;
  border-radius: var(--radius-sm);
  background: var(--color-surface-alt);
  display: inline-flex;
  gap: 8px;
  align-items: center;
  width: fit-content;
}
.status-badge[data-status="error"] {
  background: var(--color-error-bg);
  color: var(--color-error);
}
.status-badge[data-status="ready"],
.status-badge[data-status="complete"],
.status-badge[data-status="applied"] {
  background: color-mix(in srgb, var(--color-success) 12%, transparent);
  color: var(--color-success);
}
.error {
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-error);
  color: var(--color-error);
}
.clusters .cluster {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: var(--space-2);
  margin-bottom: var(--space-2);
}
.cluster header {
  display: flex;
  gap: 8px;
  align-items: baseline;
}
.cluster p {
  margin: 4px 0;
  font-size: 0.9rem;
}
.muted {
  color: var(--color-muted);
  font-size: 0.85rem;
}
.suggestion {
  border-top: 1px solid var(--color-border);
  padding-top: var(--space-2);
}
.rationale {
  font-size: 0.9rem;
  color: var(--color-muted);
}
.prompt-compare {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-2);
  margin-bottom: var(--space-2);
}
@media (max-width: 640px) {
  .prompt-compare {
    grid-template-columns: 1fr;
  }
}
.prompt-compare__label {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-muted);
  margin: 0 0 4px;
}
.prompt-compare__pre {
  margin: 0;
  font-family: var(--font-mono);
  font-size: 0.78rem;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 220px;
  overflow-y: auto;
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border);
  background: var(--color-surface-alt);
  color: var(--color-ink);
}
.prompt-compare__pre--old {
  border-left: 3px solid var(--color-error);
}
.prompt-compare__pre--new {
  border-left: 3px solid var(--color-success);
}
.prompt-editor {
  width: 100%;
  font-family: var(--font-mono);
  font-size: 0.85rem;
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border);
  background: var(--color-surface-alt);
  color: var(--color-ink);
  min-height: 250px;
}
.actions {
  margin-top: var(--space-2);
  display: flex;
  gap: var(--space-2);
}
.verdicts table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}
.verdicts th,
.verdicts td {
  border: 1px solid var(--color-border);
  padding: 6px 10px;
  text-align: left;
  vertical-align: top;
}
.verdicts th {
  background: var(--color-surface-alt);
}
.verdicts td.q {
  max-width: 30ch;
}
.verdicts td.j {
  max-width: 50ch;
}
.verdicts .link {
  background: none;
  border: none;
  color: var(--color-accent);
  cursor: pointer;
  font: inherit;
  font-size: 0.85rem;
  padding: 0;
}
.verdict-detail > td {
  background: var(--color-surface-alt);
}
.verdict-detail__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-2);
}
@media (max-width: 760px) {
  .verdict-detail__grid {
    grid-template-columns: 1fr;
  }
}
.verdict-detail__cell h5 {
  margin: 0 0 4px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-muted);
}
.verdict-detail__cell p {
  margin: 0;
  font-size: 0.85rem;
  color: var(--color-ink);
  white-space: pre-wrap;
}
.verdict-detail__cell pre {
  margin: 0;
  font-family: var(--font-mono);
  font-size: 0.78rem;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 240px;
  overflow-y: auto;
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-ink);
}
.rows .row {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: var(--space-2);
  margin-bottom: var(--space-1);
}
.rows .row header {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
}
.rows pre {
  background: var(--color-surface-alt);
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  overflow-x: auto;
  font-size: 0.8rem;
}
.spinner {
  animation: pulse 1.5s ease-in-out infinite;
}
@keyframes pulse {
  0%,
  100% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
}
</style>
